/**
 * Shared project overview payload shape (GET /work/projects/:id/overview).
 * Used by ProjectPageLayout (identity frame) and ProjectOverviewTab.
 *
 * OVERVIEW-HEALTH-UI-1: healthCode may be NOT_ENOUGH_DATA (M8a). Missing or
 * unknown codes stay null — never coerced to HEALTHY.
 */

export interface NeedsAttentionItem {
  typeCode: string;
  reasonText: string;
  ownerUserId: string | null;
  nextStepCode: string;
  nextStepLabel: string;
  entityRef: {
    taskId?: string;
    phaseId?: string;
  };
  dueDate?: string;
}

/** Known overview health codes from the server. null = missing/unknown. */
export type ProjectHealthCode =
  | 'HEALTHY'
  | 'AT_RISK'
  | 'BLOCKED'
  | 'NOT_ENOUGH_DATA'
  // DRAFT-TRUTH-1: a Draft is not started, so it is not rated.
  | 'NOT_RATED';

const KNOWN_HEALTH_CODES = new Set<string>([
  'HEALTHY',
  'AT_RISK',
  'BLOCKED',
  'NOT_ENOUGH_DATA',
  'NOT_RATED',
]);

/**
 * DRAFT-TRUTH-1: the server's Draft facts (present only for a DRAFT project).
 * notStarted ⇒ NOT_RATED; workUnderWay ⇒ "Draft · work under way", rated
 * normally (TR-2026-10-22-20).
 */
export interface DraftTruth {
  notStarted: boolean;
  workUnderWay: boolean;
  healthRated: boolean;
  /** false ⇒ this Draft's tasks are not counted as overdue (its work starts at Start). */
  overdueCounted: boolean;
  datesArePlanned: true;
}

function parseDraftTruth(raw: unknown): DraftTruth | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.notStarted !== 'boolean') return null;
  return {
    notStarted: r.notStarted,
    workUnderWay: r.workUnderWay === true,
    healthRated: r.healthRated === true,
    overdueCounted: r.overdueCounted === true,
    datesArePlanned: true,
  };
}

/**
 * Parse a wire healthCode. Unknown or absent → null (Health unavailable).
 * Never defaults to HEALTHY.
 */
export function parseProjectHealthCode(raw: unknown): ProjectHealthCode | null {
  if (typeof raw !== 'string') return null;
  if (KNOWN_HEALTH_CODES.has(raw)) return raw as ProjectHealthCode;
  return null;
}

export interface ProjectOverview {
  projectId: string;
  projectName: string;
  projectState: string;
  structureLocked: boolean;
  startedAt: string | null;
  deliveryOwnerUserId: string | null;
  dateRange: {
    startDate: string | null;
    dueDate: string | null;
  };
  /** null when the payload omitted health or sent an unknown code. */
  healthCode: ProjectHealthCode | null;
  healthLabel: string;
  /** DRAFT-TRUTH-1: null unless the server sent the Draft block. */
  draft: DraftTruth | null;
  behindTargetDays: number | null;
  needsAttention: NeedsAttentionItem[];
  nextActions: NeedsAttentionItem[];
  /**
   * Server rollup (COUNTS-LEAF-1 / D-85: the same predicate as the task list's
   * ?overdue / ?dueThisWeek / ?unassigned filters). Absent or null when the
   * payload omitted it; a missing count is null — never zero.
   */
  taskRollup?: TaskRollupCounts | null;
}

export function overviewActionItemKey(item: NeedsAttentionItem): string {
  return (
    item.entityRef?.taskId ??
    `${item.typeCode}:${item.reasonText}:${item.nextStepLabel}`
  );
}

export function normalizeProjectOverview(payload: unknown): ProjectOverview | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Partial<ProjectOverview> & { healthCode?: unknown };
  const healthCode = parseProjectHealthCode(raw.healthCode);
  return {
    projectId: raw.projectId ?? '',
    projectName: raw.projectName ?? '',
    projectState: raw.projectState ?? 'DRAFT',
    structureLocked: Boolean(raw.structureLocked),
    startedAt: raw.startedAt ?? null,
    deliveryOwnerUserId: raw.deliveryOwnerUserId ?? null,
    dateRange: {
      startDate: raw.dateRange?.startDate ?? null,
      dueDate: raw.dateRange?.dueDate ?? null,
    },
    healthCode,
    // Prefer server label; when unavailable, leave blank — the badge owns the copy.
    healthLabel:
      typeof raw.healthLabel === 'string' ? raw.healthLabel : '',
    draft: parseDraftTruth((raw as { draft?: unknown }).draft),
    behindTargetDays: raw.behindTargetDays ?? null,
    needsAttention: Array.isArray(raw.needsAttention) ? raw.needsAttention : [],
    nextActions: Array.isArray(raw.nextActions) ? raw.nextActions : [],
    taskRollup: parseTaskRollup(raw.taskRollup),
  };
}

export interface TaskRollupCounts {
  overdueTasks: number | null;
  dueThisWeekTasks: number | null;
  unassignedTasks: number | null;
  /**
   * F42 (TR-2026-11-02-15): project-wide LEAF-task totals from the server, not a
   * count over the capped client list. total = done + cancelled + open. Any of
   * these absent from the payload is null, never 0.
   */
  totalTasks: number | null;
  doneTasks: number | null;
  cancelledTasks: number | null;
  openTasks: number | null;
  /** null = NOT_ENOUGH_DATA (the server's rule); never render it as 0. */
  completionPercent: number | null;
}

function countOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function parseTaskRollup(raw: unknown): TaskRollupCounts | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const counts: TaskRollupCounts = {
    overdueTasks: countOrNull(r.overdueTasks),
    dueThisWeekTasks: countOrNull(r.dueThisWeekTasks),
    unassignedTasks: countOrNull(r.unassignedTasks),
    totalTasks: countOrNull(r.totalTasks),
    doneTasks: countOrNull(r.doneTasks),
    cancelledTasks: countOrNull(r.cancelledTasks),
    openTasks: countOrNull(r.openTasks),
    completionPercent: countOrNull(r.completionPercent),
  };
  if (Object.values(counts).every((v) => v === null)) return null;
  return counts;
}
